import { GraphQL } from '@guestgoodie/graphql'
import { BedIcon, Delete01Icon } from '@hugeicons/core-solid-rounded'
import { Trans, useLingui } from '@lingui/react/macro'

import * as Form from '@/features/form'
import Button from '@/components/ui/Button'
import Textarea from '@/components/ui/Textarea'
import Input from '@/components/ui/Input'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { useIsPmsConnected } from '@/features/integrations'
import useSpaceForm from '../hooks/useSpaceForm'
import { Permission, useCheckPermission } from '@/features/auth'

type CreateProps = {
  onDone: () => void
  onCancel: () => void
}

type EditProps = {
  space: GraphQL.SpaceFragmentFragment
  onDone: () => void
  onCancel: () => void
}

type Props = CreateProps | EditProps

type FormData = {
  identifier: string
  name: string
  description: string
}

export default function SpaceForm(props: Props) {
  const { onDone, onCancel } = props
  const space = (props as EditProps).space ?? null

  const hasUpdatePermission = useCheckPermission([Permission.SPACE_UPDATE])
  const hasDeletePermission = useCheckPermission([Permission.SPACE_DELETE])
  const isConnectedToPms = useIsPmsConnected()
  const canUpdateSpace = hasUpdatePermission && !isConnectedToPms
  const canDeleteSpace = hasDeletePermission && !isConnectedToPms

  const { t } = useLingui()

  const { errors, create, update, remove } = useSpaceForm({
    id: space?.id,
    onDone
  })

  const identifier = space?.identifier

  return (
    <Form.Root<FormData>
      onSubmit={space ? update : create}
      serverErrors={errors}
      readOnly={!canUpdateSpace}
      defaultValues={{
        identifier: space?.identifier ?? '',
        name: space?.name ?? '',
        description: space?.description ?? ''
      }}
    >
      <div className="flex flex-col gap-7 p-2.5">
        <div className="flex flex-row items-start gap-4">
          {!space && (
            <Form.Field
              name="identifier"
              label={`${t`Room Identifier (e.g.: Room Number)`}*`}
              rules={{
                maxLength: {
                  value: 255,
                  message: t`Can't be longer than 255 characters.`
                },
                validate: {
                  required: Form.Validations.requiredWithTrimValidation(
                    t`This field is required`
                  )
                }
              }}
            >
              {({ renderProps: { onChange, ...props } }) => (
                <Input
                  autoFocus
                  onChange={event => onChange(event.target.value)}
                  type="text"
                  placeholder={t({
                    comment:
                      'Placeholder for the input of the room identifier of a room',
                    message: 'e.g.: 101'
                  })}
                  Icon={BedIcon}
                  {...props}
                />
              )}
            </Form.Field>
          )}
          <Form.Field
            name="name"
            label={`${t`Room Name`}*`}
            rules={{
              maxLength: {
                value: 255,
                message: t`Can't be longer than 255 characters.`
              },
              validate: {
                required: Form.Validations.requiredWithTrimValidation(
                  t`This field is required`
                )
              }
            }}
          >
            {({ renderProps: { onChange, ...props } }) => (
              <Input
                type="text"
                autoFocus
                onChange={event => onChange(event.target.value)}
                placeholder={t({
                  comment: 'Placeholder for the input of the room name',
                  message: 'e.g.: Deluxe Room'
                })}
                {...props}
              />
            )}
          </Form.Field>
        </div>
        <Form.Field name="description" label={t`Room Description`}>
          {({ renderProps: { onChange, ...props } }) => (
            <Textarea
              placeholder={t({
                comment: 'Placeholder for the input of the room description',
                message: 'e.g.: Large room with garden view'
              })}
              onChange={event => onChange(event.target.value)}
              {...props}
            />
          )}
        </Form.Field>
        <div className="flex flex-row justify-center">
          <Form.Error name="root.serverError" />
        </div>
        <footer className="flex flex-row items-between justify-between gap-4">
          <div className="flex flex-row items-center gap-2.5">
            <Button variant="outline" onClick={onCancel} type="button">
              <Trans>Cancel</Trans>
            </Button>
            {space && canDeleteSpace && (
              <ConfirmDialog
                title={t`Delete Room`}
                message={t`Are you sure you want to delete the room with the identifier "${identifier}", this cannot be reversed?`}
                onConfirm={remove}
                asChild
              >
                <Button type="button" variant="outline" Icon={Delete01Icon}>
                  <Trans>Delete</Trans>
                </Button>
              </ConfirmDialog>
            )}
          </div>
          <div className="flex flex-row items-center gap-4">
            <Form.Submit>
              <Trans>Save</Trans>
            </Form.Submit>
          </div>
        </footer>
      </div>
    </Form.Root>
  )
}
